import { useState, useEffect } from "react";
import {
  Button,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  PopoverBody,
  Row,
  UncontrolledDropdown,
  UncontrolledPopover,
} from "reactstrap";
import ButtonBack from "../../../../Components/ButtonBack";

import brokerIcon from "../../../../assets/images/iconsRoles/brokerIcon.svg";
import employerIcon from "../../../../assets/images/iconsRoles/employerGroupIcon.svg";
import gaIcon from "../../../../assets/images/iconsRoles/GaIcon.svg";
import nationalIcon from "../../../../assets/images/iconsRoles/nationalIcon.svg";
import UserStatusBadge from "../../../../Components/Badges/UserStatusBadge";
import EditEmployeeModal from "../../../../Components/modals/EmployeesModal/EditEmployeeModal";
import UserInfoCard from "../../../../Components/UserInfoCard/UserInfoCard";
import { DASHBOARD_USER_ROLES, INDIVIDUAL_STATUS } from "../../../../constants";
import { useReferralLinkBuilder } from "../../../../hooks/useReferralLinkBuilder";
import { formatDate } from "../../../../utils/utils";

const ProfileIndividual = ({
  onRefreshTable,
  individual,
  loading,
  onDelete,
  setIndividual,
  onResendInvite,
}) => {

  const [openEditModal, setOpenEditModal] = useState(false);

  const isTerminated = individual?.status === "inactive";
  const [isCodeCopied, setIsCodeCopied] = useState(false);
  const [referralLink, setReferralLink] = useState("");

  const { createIndividualUniqueLink, fetchIndividualEnrollmentLink } = useReferralLinkBuilder();

  // Lógica actual del frontend
  const frontendGeneratedLink = individual ? createIndividualUniqueLink(individual.individualCode, individual.agentCode) : "";

  // Fetch enrollment link from backend when component mounts or individual changes
  useEffect(() => {
    const fetchEnrollmentLink = async () => {
      if (!individual?.id) {
        // Si no hay individual, usar la lógica del frontend
        setReferralLink(frontendGeneratedLink);
        console.log("📋 [FRONTEND ONLY] No individual ID, using frontend logic:", {
          frontendLink: frontendGeneratedLink,
          individualCode: individual?.individualCode,
          agentCode: individual?.agentCode
        });
        return;
      }

      try {
        // Intentar obtener el link desde el backend
        const data = await fetchIndividualEnrollmentLink(individual.id);

        setReferralLink(data.enrollmentLink);

        // Console log para comparar ambos métodos
        console.log("🔍 [COMPARISON] Enrollment Link Methods:", {
          backend: {
            fullResponse: data,
            enrollmentLink: data.enrollmentLink,
            agentCode: data.agentCode,
            individualCode: data.individualCode,
            individualId: data.individualId,
            dashboardUserId: data.dashboardUserId
          },
          frontend: {
            generatedLink: frontendGeneratedLink,
            individualCode: individual.individualCode,
            agentCode: individual.agentCode
          },
          linksMatch: data.enrollmentLink === frontendGeneratedLink,
          comparison: {
            agentCodeMatch: data.agentCode === individual.agentCode,
            individualCodeMatch: data.individualCode === individual.individualCode
          }
        });

      } catch (error) {
        // Si el backend falla, usar la lógica del frontend como fallback
        const errorMessage = error.message || error;
        const isAgentCodeMissing = errorMessage.includes("does not have an agent code");

        console.log("⚠️ [FALLBACK] Backend failed, using frontend logic:", {
          reason: isAgentCodeMissing ? "Agent code not configured in backend" : "API error",
          error: errorMessage,
          fallbackLink: frontendGeneratedLink,
          individualCode: individual.individualCode,
          agentCode: individual.agentCode,
          note: isAgentCodeMissing
            ? "This is expected - the dashboard user needs an agentCode configured in the backend, or you can continue using the frontend-generated link."
            : "Unexpected error occurred"
        });
        setReferralLink(frontendGeneratedLink);
      }
    };

    fetchEnrollmentLink();
  }, [individual, frontendGeneratedLink, fetchIndividualEnrollmentLink]);

  const linkToProfile = (role) => {
    switch (role) {
      case DASHBOARD_USER_ROLES.NATIONAL:
        return "national";
      case DASHBOARD_USER_ROLES.GA:
        return "ga";
      case DASHBOARD_USER_ROLES.BROKER:
        return "brokers";
      case DASHBOARD_USER_ROLES.EMPLOYER:
        return "employers";
      default:
        return "";
    }
  };

  const renderIcon = (role) => {
    switch (role) {
      case DASHBOARD_USER_ROLES.NATIONAL:
        return nationalIcon;
      case DASHBOARD_USER_ROLES.GA:
        return gaIcon;
      case DASHBOARD_USER_ROLES.BROKER:
        return brokerIcon;
      case DASHBOARD_USER_ROLES.EMPLOYER:
        return employerIcon;
      default:
        return "";
    }
  };

  const handleCopyInviteCode = () => {
    setIsCodeCopied(true);
    navigator.clipboard.writeText(referralLink);
    setTimeout(() => {
      setIsCodeCopied(false);
    }, 3000);
  };
  const handleEditClick = () => {
    setOpenEditModal(true);
  };

  const handleRemoveIndividual = () => {
    onDelete(individual);
  };

  const handleResendInvite = (item) => {
    onResendInvite(item);
  };

  const renderDropdownMenu = (item) => {
    return (
      <DropdownMenu>
        {individual.status === "invited" && (
          <DropdownItem
            onClick={() => handleResendInvite(item)}
            className="edit-item-btn"
          >
            <i className="ri-mail-send-fill align-bottom me-2 text-muted"></i>
            Resend Invite
          </DropdownItem>
        )}
        <DropdownItem
          onClick={() => handleEditClick(item)}
          className="edit-item-btn"
        >
          <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>
          Edit
        </DropdownItem>
        {isTerminated && (
          <DropdownItem
            onClick={() => handleRemoveIndividual(item)}
            className="remove-item-btn"
          >
            {" "}
            <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>{" "}
            Delete Completely
          </DropdownItem>
        )}
        {!isTerminated && (
          <DropdownItem
            onClick={() => { }}
            className="d-flex align-items-center"
          >
            <i className="ri-close-line ms-n1 me-2 fs-4 text-muted "></i>
            Terminate
          </DropdownItem>
        )}
      </DropdownMenu>
    );
  };

  if (loading || !individual) {
    return (
      <div className="page-content">
        <div
          className="text-center d-flex justify-content-center align-items-center"
          style={{ minHeight: "150px", height: "150px" }}
        >
          <div className="spinner-border spinner-border-xl mr-3"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      {openEditModal && (
        <EditEmployeeModal
          isIndividual
          open={openEditModal}
          editUser={individual}
          setOpen={setOpenEditModal}
          setUser={setIndividual}
          onRefreshTable={onRefreshTable}
        />
      )}
      <div className="page-content">
        <div className="card">
          <div className="card-body pb-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <ButtonBack />
              <UncontrolledDropdown className="dropdown d-inline-block">
                <DropdownToggle
                  tag="button"
                  className="btn btn-soft-primary fw-normal border border-primary btn-xl"
                >
                  Manage
                </DropdownToggle>
                {renderDropdownMenu(individual)}
              </UncontrolledDropdown>
            </div>
            <>
              <Row className="d-flex align-items-start">
                <Col xl={4}>
                  <Col xl={7}>
                    <div className=" border border-1 rounded">
                      <div
                        style={{ background: "var(--thead-bg)" }}
                      >
                        <h5 className="mb-0 p-3 ">
                          Individual: {individual.firstName}{" "}
                          {individual.lastName}
                        </h5>
                      </div>
                      <div className=" px-3 pt-4 pb-0">
                        <h5 className="fw-semibold">
                          {individual.firstName} {individual.lastName}
                        </h5>

                        <h6 className="mb-0 fw-normal text-muted">
                          {individual.email}
                        </h6>
                      </div>
                      <hr className="text-muted mx-3" />
                      <div className=" p-3">
                        <h6 className="fw-normal">
                          {individual.individualCode
                            ? "Date Added on"
                            : "Invited On"}
                          : {formatDate(individual.createdAt)}
                        </h6>
                        <h6 className="fw-normal">
                          Status:{" "}
                          <UserStatusBadge
                            status={
                              individual.status === INDIVIDUAL_STATUS.ACTIVE
                                ? "Active"
                                : individual.status ===
                                  INDIVIDUAL_STATUS.INVITED
                                  ? "Invited"
                                  : "Inactive"
                            }
                            color={
                              individual.status === INDIVIDUAL_STATUS.ACTIVE
                                ? "success"
                                : individual.status ===
                                  INDIVIDUAL_STATUS.INVITED
                                  ? "info"
                                  : "danger"
                            }
                          />
                        </h6>
                      </div>
                    </div>
                  </Col>
                </Col>
                <Col xl={6} className="mt-4 mt-xxl-0">
                  <Col xl={8}>
                    {/* {individual.userInfo && (
                      <UserInfoCard
                        info={individual.userInfo}
                        role={individual.userInfo.role}
                        linkToProfile={linkToProfile}
                        invitedByUserId={individual.invitedByUserId}
                        icon={renderIcon(individual.userInfo.role)}
                      />
                    )} */}
                    {individual.nationalInfo && (
                      <UserInfoCard
                        info={individual.nationalInfo}
                        role={individual.nationalInfo.role}
                        linkToProfile={linkToProfile}
                        invitedByUserId={individual.invitedByUserId}
                        icon={renderIcon(individual.nationalInfo.role)}
                      />
                    )}
                    {individual.gaInfo && (
                      <UserInfoCard
                        info={individual.gaInfo}
                        role={individual.gaInfo.role}
                        linkToProfile={linkToProfile}
                        invitedByUserId={individual.invitedByUserId}
                        icon={renderIcon(individual.gaInfo.role)}
                      />
                    )}
                    {individual.brokerInfo && (
                      <UserInfoCard
                        info={individual.brokerInfo}
                        role={individual.brokerInfo.role}
                        linkToProfile={linkToProfile}
                        invitedByUserId={individual.invitedByUserId}
                        icon={renderIcon(individual.brokerInfo.role)}
                      />
                    )}

                    {referralLink && (
                      <div className="w-100 mb-4">
                        <h5 className="mb-2 ms-1 fw-semibold">Individual Unique Link:</h5>
                        <div className="border rounded-4 p-3 d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 bg-light">
                          <div className="text-break text-muted flex-grow-1">
                            <a target="_blank" href={referralLink}>
                              {referralLink}
                            </a>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <Button
                              id="copyInviteCode"
                              onClick={handleCopyInviteCode}
                              color="soft-primary"
                              size="sm"
                              className="border border-primary d-flex align-items-center"
                            >
                              {isCodeCopied ? (
                                <>
                                  <i className="ri-check-line me-2" />
                                  Copied!
                                </>
                              ) : (
                                <>
                                  <i className="ri-file-copy-line me-2" />
                                  Copy
                                </>
                              )}
                            </Button>
                            <UncontrolledPopover
                              onClick={(e) => e.stopPropagation()}
                              placement="bottom"
                              target="copyInviteCode"
                              trigger="hover"
                            >
                              <PopoverBody className="p-2">
                                {isCodeCopied ? "Copied!" : "Copy to clipboard"}
                              </PopoverBody>
                            </UncontrolledPopover>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* {individual.brokerInfo && (
                      <>
                        <h5 className="mb-2 ms-1">Broker:</h5>
                        <div className="border rounded-4 p-3">
                          <span className="text-muted">
                            {individual.brokerInfo.firstName}{" "}
                            {individual.brokerInfo.lastName}{" "}
                          </span>
                        </div>
                      </>
                    )} */}
                  </Col>
                </Col>
                <Col xl={2}>
                  <div className="d-flex justify-content-end top-0">
                    <Button
                      onClick={() => handleEditClick(individual)}
                      color="primary"
                    >
                      Edit Information
                    </Button>
                  </div>
                </Col>
              </Row>
            </>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfileIndividual;
